// SessionStateDetector — the shared "who am I" detector
// (plans/2026-07-04--native-app-account-persistence.md, Phase 4).
//
// THIS MODULE IS IMPORTED BY `packs/global.js` AND NOTHING ELSE
// (plans/2026-07-14--session-detector-namespace-promotion.md). esbuild runs with
// `splitting: false` / `format: "iife"`, so every module a pack imports is INLINED into that
// pack's bundle — importing this from a component would ship a second copy of the detector in
// that component's bundle. Consumers import the `lib/session_state` shim instead, which forwards
// to the one instance global.js parks on the Rankers namespace.
//
// An independent utility (not a UI component), living alongside tracker.js. It
// fetches the session-state endpoint, diffs the result against the last-known
// state (persisted in localStorage), and on a *change*:
//   • emits the `Person-loggedIn` / `Person-loggedOut` native signal (via an
//     injected emitter — a no-op on pure web where no native host exists), and
//   • notifies subscribed components (e.g. AccountPanel).
//
// It catches EVERY login-state path — OTP verify, password login, rankometer /
// ferry-discount auto-login, logout, and silent 60-day expiry — because it
// compares the live endpoint against the stored state on each check().
//
// Cookie-store-split constraint: the endpoint URL MUST be an absolute
// https://www.rankers.co.nz URL, fetched as a credentialed GET WITHOUT the
// X-Rankers-User-Agent header (markers-API precedent — a CORS "simple request",
// no preflight), so it is a direct-HTTPS JS fetch that carries the WKWebView /
// CookieManager `itchi` cookie rather than an intercepted cookieless request.

import {getRankersNamespace} from "../constants/rankers_namespace";
import {SESSION_CHANGED_EVENT} from "../constants/session_events";

export const SessionSignals = {
  loggedIn: "Person-loggedIn",
  loggedOut: "Person-loggedOut",
};

const DEFAULT_STORAGE_KEY = "RANKERS__SESSION_STATE";

export class SessionStateDetector {
  constructor(options = {}) {
    this.subscribers = new Set();
    this.storageKey = options.storageKey || DEFAULT_STORAGE_KEY;
    this.endpointUrl = options.endpointUrl || null;
    this.signalEmitter = options.signalEmitter || null;
    // `undefined` = never checked; `null` = known logged-out; object = logged-in.
    this.lastKnown = this.readStored();
    // Has the endpoint answered in THIS page's lifetime? `lastKnown` is seeded from
    // localStorage and can be stale, so a consumer about to do something destructive on
    // the strength of "logged out" (clearing an identity-scoped store) must wait for a
    // server-confirmed state rather than trust the seed.
    this.hasChecked = false;
  }

  // Fill in endpoint / emitter when a later caller has them and the first didn't
  // (the shared singleton is configured by whichever consumer loads first).
  configure(options = {}) {
    if (!this.endpointUrl && options.endpointUrl) this.endpointUrl = options.endpointUrl;
    if (!this.signalEmitter && options.signalEmitter) this.signalEmitter = options.signalEmitter;
    return this;
  }

  // Register a subscriber. It is immediately called with the current known state
  // (unless we've never checked), so late subscribers render correctly.
  subscribe(callback) {
    this.subscribers.add(callback);
    if (typeof this.lastKnown !== "undefined") callback(this.lastKnown);
    return () => this.subscribers.delete(callback);
  }

  // Fetch the endpoint, diff, emit + notify on change. Resolves regardless of
  // outcome; a network failure is a silent no-op (offline → keep current view).
  //
  // `emitToNative` (default true): a poke-triggered re-check (native fan-out) passes
  // false — native already knows, so we sync UI without re-emitting, keeping the
  // fan-out one-way and echo-free. Genuine detections keep the default true.
  check(options = {}) {
    const { emitToNative = true } = options;
    if (!this.endpointUrl) return Promise.resolve();
    return fetch(this.endpointUrl, {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (data === null) return; // non-OK response; leave state untouched
        const next = data.person_id
          ? { personId: data.person_id, displayName: data.display_name, email: data.email, name: data.name }
          : null;
        this.applyState(next, emitToNative);
      })
      .catch(() => {
        /* offline / unreachable — no-op */
      });
  }

  applyState(next, emitToNative = true) {
    const prev = this.lastKnown;
    const changed = this.isTransition(prev, next);

    this.lastKnown = next;
    this.hasChecked = true; // only reached from a real endpoint response
    this.writeStored(next);

    if (changed) {
      if (emitToNative) this.emitForTransition(prev, next);
      // Broadcast the in-page session-change event on a real transition, regardless of
      // emitToNative — a fan-out-poke re-check (emitToNative:false) that reveals a change
      // must still update in-page UI (e.g. AccountPanel) even though it doesn't re-emit to
      // native. No-op re-checks (no transition) dispatch nothing.
      this.dispatchSessionChanged(next);
    }
    // Retained subscribe/notify path (idempotent — fires on every check). AccountPanel now
    // uses the SESSION_CHANGED_EVENT document event instead; kept for any external observer.
    this.notify(next);
  }

  dispatchSessionChanged(state) {
    try {
      document.dispatchEvent(new CustomEvent(SESSION_CHANGED_EVENT, { detail: state }));
    } catch (e) {
      /* no DOM (e.g. SSR / unit env without document) — the notify() path still applies */
    }
  }

  // A transition worth signalling. `undefined` (never checked) counts as
  // logged-out for emit purposes: undefined→logged-in emits loggedIn;
  // undefined→null (never logged in) emits nothing.
  isTransition(prev, next) {
    const prevId = prev && prev.personId;
    const nextId = next && next.personId;
    if (nextId && prevId !== nextId) return true; // logged in / switched person
    if (!nextId && prevId) return true; // logged out / expired
    return false;
  }

  emitForTransition(prev, next) {
    if (!this.signalEmitter) return; // pure web — no native host to receive it
    if (next && next.personId) {
      this.signalEmitter.emit(SessionSignals.loggedIn, { person_id: next.personId });
    } else {
      this.signalEmitter.emit(SessionSignals.loggedOut);
    }
  }

  notify(state) {
    this.subscribers.forEach((callback) => {
      try {
        callback(state);
      } catch (e) {
        console.error("[SessionStateDetector] subscriber error", e);
      }
    });
  }

  readStored() {
    try {
      const raw = window.localStorage.getItem(this.storageKey);
      return raw === null ? undefined : JSON.parse(raw);
    } catch (e) {
      return undefined;
    }
  }

  writeStored(state) {
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify(state));
    } catch (e) {
      /* storage unavailable — the detector still works in-memory */
    }
  }
}

// The site-wide shared detector, so the nav boot (shell page-load, native emitter), the
// Rankometer boot (standalone review webview) and AccountPanel operate on ONE instance with
// one localStorage state. Held on the Rankers namespace (getRankersNamespace().sessionStateDetector)
// — migrated off the pre-namespace raw global window.__rankersSessionStateDetector so the
// thin global.js announceSessionChange() stub can find it without importing this module.
export function getSharedSessionStateDetector(options = {}) {
  const ns = getRankersNamespace();
  let detector = ns.sessionStateDetector;
  if (!detector) {
    detector = new SessionStateDetector(options);
    ns.sessionStateDetector = detector;
  } else {
    detector.configure(options);
  }
  return detector;
}

// The seed-then-listen contract every login-aware UI wants: render the CURRENT state
// immediately, then re-render on every REAL transition (login, logout, person-switch,
// silent expiry), and kick a check so the state is fresh.
//
// By default this observes SESSION_CHANGED_EVENT, which fires only on a transition —
// preferable to detector.subscribe(), which looks equivalent but notifies on EVERY
// check() including no-op re-checks that changed nothing.
//
// `onEveryCheck: true` opts INTO those no-op checks (via subscribe). A consumer that
// re-validates persisted state against the live session needs them: a page load after a
// logout that happened on some other page sees logged-out → logged-out, which is no
// transition at all and therefore raises no event — yet the persisted state it left
// behind still has to be reconciled. Pair it with `detector.hasChecked` before acting
// destructively, since the first call is the (possibly stale) localStorage seed.
//
// Returns the shared `detector` (callers re-check it after their own logout / auth /
// accept) and a `stop()` that detaches.
export function observeSessionState(handler, options = {}) {
  const { onEveryCheck = false, ...detectorOptions } = options;
  const detector = getSharedSessionStateDetector(detectorOptions);
  let stop;

  if (onEveryCheck) {
    // subscribe() seeds with lastKnown (unless never checked) and then fires on every check.
    stop = detector.subscribe(handler);
  } else {
    // `undefined` = never checked (nothing to render yet); `null` = known logged-out.
    if (typeof detector.lastKnown !== "undefined") handler(detector.lastKnown);

    const listener = (event) => handler(event.detail);
    document.addEventListener(SESSION_CHANGED_EVENT, listener);
    stop = () => document.removeEventListener(SESSION_CHANGED_EVENT, listener);
  }

  detector.check();

  return {
    detector: detector,
    stop: stop,
  };
}
